<template>
  <div class="gpx-animator">
    <div class="container">
      <div class="sidebar">
        <h2>{{ $t('makeKorlor.title') }}</h2>

        <div class="file-input">
          <label for="gpxFile">{{ $t('makeKorlor.uploadGpx') }}:</label>
          <input type="file" id="gpxFile" accept=".gpx" @change="handleGpxFile" />
        </div>

        <div class="music-input">
          <label for="musicFile">{{ $t('makeKorlor.addMusic') }}:</label>
          <input type="file" id="musicFile" accept="audio/*" @change="handleMusicFile" />
        </div>

        <div class="track-info" v-if="trackStats.points">
          <h3>{{ $t('makeKorlor.trackInfo') }}</h3>
          <div class="track-details">
            <p><strong>Points:</strong> {{ trackStats.points.length }}</p>
            <p><strong>{{ $t('makeKorlor.startPoint') }}:</strong> {{ formatDateTime(trackStats.points[0] && trackStats.points[0].time) }}</p>
            <p><strong>{{ $t('makeKorlor.endPoint') }}:</strong> {{ formatDateTime(trackStats.points[trackStats.points.length-1] && trackStats.points[trackStats.points.length-1].time) }}</p>
          </div>
        </div>

        <div class="stats" v-if="trackStats.points">
          <div class="stat-card">
            <div class="stat-value">{{ formatDistance(trackStats.totalDistance) }}</div>
            <div class="stat-label">{{ $t('makeKorlor.totalDistance') }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">{{ formatTime(trackStats.totalTime) }}</div>
            <div class="stat-label">{{ $t('makeKorlor.totalTime') }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">{{ formatSpeed(trackStats.avgSpeed) }}</div>
            <div class="stat-label">{{ $t('makeKorlor.avgSpeed') }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">{{ formatSpeed(trackStats.maxSpeed) }}</div>
            <div class="stat-label">{{ $t('makeKorlor.maxSpeed') }}</div>
          </div>
        </div>

        <div class="animation-controls">
          <button class="btn play-btn" @click="playAnimation" :disabled="!trackPoints.length">
            <span class="btn-icon">▶️</span>
            <span class="btn-text">{{ $t('makeKorlor.play') }}</span>
          </button>
          <button class="btn pause-btn" @click="pauseAnimation" :disabled="!isPlaying">
            <span class="btn-icon">⏸️</span>
            <span class="btn-text">{{ $t('makeKorlor.pause') }}</span>
          </button>
          <button class="btn reset-btn" @click="resetAnimation" :disabled="!trackPoints.length">
            <span class="btn-icon">🔄</span>
            <span class="btn-text">{{ $t('makeKorlor.reset') }}</span>
          </button>
          <button class="btn record-btn" @click="toggleRecording" :disabled="!trackPoints.length" :class="{ 'recording': isRecording }">
            <span class="btn-icon">{{ isRecording ? '⏹️' : '🎥' }}</span>
            <span class="btn-text">{{ isRecording ? $t('makeKorlor.stopRecording') : $t('makeKorlor.recordVideo') }}</span>
          </button>
        </div>

        <div class="recording-info" v-if="isRecording">
          <div class="recording-indicator">
            <span class="recording-dot"></span>
            {{ $t('makeKorlor.recording') }} {{ recordingDuration }}
          </div>
          <div class="recording-instructions">
            <p>🎯 <strong>{{ $t('makeKorlor.autoSelecting') }}:</strong> {{ $t('makeKorlor.shouldAutoRecord') }}</p>
            <p>📱 <strong>{{ $t('makeKorlor.ifPrompted') }}:</strong> {{ $t('makeKorlor.selectThisTab') }}</p>
          </div>
          <div class="force-stop-section">
            <button class="btn force-stop-btn" @click="forceStopRecording" :title="$t('makeKorlor.forceStopRecording')">
              <span class="btn-icon">🛑</span>
              <span class="btn-text">{{ $t('makeKorlor.forceStop') }}</span>
            </button>
          </div>
        </div>

        <div class="video-controls" v-if="recordedVideoUrl">
          <h4>{{ $t('makeKorlor.recordedVideo') }}</h4>
          <video ref="recordedVideo" controls style="width: 100%; max-width: 300px; margin: 10px 0;">
            <source :src="recordedVideoUrl" type="video/webm">
            {{ $t('makeKorlor.videoNotSupported') }}
          </video>
          <div class="video-actions">
            <button class="btn download-btn" @click="downloadVideo">
              <span class="btn-icon">📥</span>
              <span class="btn-text">{{ $t('makeKorlor.downloadVideo') }}</span>
            </button>
            <button class="btn share-btn" @click="shareVideo" :disabled="!shareUrl">
              <span class="btn-icon">🔗</span>
              <span class="btn-text">{{ $t('makeKorlor.shareVideo') }}</span>
            </button>
          </div>
        </div>

        <div class="slider-container">
          <label for="speedSlider">{{ $t('makeKorlor.animationSpeed') }}</label>
          <input
            type="range"
            id="speedSlider"
            class="slider"
            min="1"
            max="20"
            step="1"
            v-model="animationSpeed"
            @input="updateAnimationSpeed"
          >
          <span>{{ speedValue }}</span>
        </div>

        <div class="slider-container">
          <label for="heightSlider">{{ $t('makeKorlor.markerSize') }}</label>
          <input
            type="range"
            id="heightSlider"
            class="slider"
            min="12"
            max="48"
            step="2"
            v-model="markerSize"
            @input="updateMarkerSize"
          >
          <span>{{ markerSize }}px</span>
        </div>

        <div class="progress-container">
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: progressWidth + '%' }"></div>
          </div>
          <div>{{ Math.round(progressWidth) }}%</div>
        </div>

        <div class="loading" v-if="loading">
          <div class="spinner"></div>
          <p>{{ $t('makeKorlor.loading') }}</p>
        </div>

        <div id="messages">
          <div v-for="(message, index) in messages" :key="index" :class="message.type">
            {{ message.text }}
          </div>
        </div>
      </div>

      <div class="main-content">
        <div id="map"></div>

        <div class="controls">
          <button class="btn map-btn" @click="toggleSatellite" title="Satellite">
            <span class="btn-icon">🛰️</span>
          </button>
          <button class="btn map-btn" @click="toggleTerrain" title="Terrain">
            <span class="btn-icon">🏔️</span>
          </button>
          <button class="btn map-btn" @click="toggleStreet" title="Street">
            <span class="btn-icon">🗺️</span>
          </button>
        </div>

        <div class="speed-display" v-if="showSpeedDisplay">
          <div>{{ $t('makeKorlor.currentSpeed') }} <span>{{ formatSpeed(currentSpeed) }}</span></div>
          <div>{{ $t('makeKorlor.elevation') }} <span>{{ formatElevation(currentElevation) }}</span></div>
          <div>{{ $t('makeKorlor.distance') }} <span>{{ formatDistance(currentDistance) }}</span></div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import * as L from 'leaflet'
import 'leaflet/dist/leaflet.css'

export default {
  name: 'MakeKorlor',
  data () {
    return {
      L: null,
      map: null,
      trackPoints: [],
      currentIndex: 0,
      animationId: null,
      isPlaying: false,
      animationSpeed: 1,
      markerSize: 24,
      audioElement: null,
      trackStats: {},
      currentMarker: null,
      trackPolyline: null,
      startMarker: null,
      endMarker: null,
      currentMapStyle: 'street',
      loading: false,
      messages: [],
      currentSpeed: 0,
      currentElevation: 0,
      currentDistance: 0,
      showSpeedDisplay: false,
      progressWidth: 0,
      speedEffectActive: false,
      speedEffectTimer: null,
      musicRepeatCount: 0,
      isRecording: false,
      recordedVideoUrl: null,
      recordingDuration: '00:00',
      mediaRecorder: null,
      recordedChunks: [],
      shareUrl: null,
      recordingStartTime: 0,
      recordingDurationInterval: null
    }
  },
  computed: {
    speedValue () {
      return `${this.animationSpeed}x`
    }
  },
  mounted () {
    this.loadLeaflet()

    // Load default music
    this.loadDefaultMusic()
  },
  beforeDestroy () {
    this.cleanup()
  },
  methods: {
    loadLeaflet () {
      try {
        this.L = L
        this.initMap()
      } catch (error) {
        console.error('Failed to load Leaflet:', error)
      }
    },
    initMap () {
      this.$nextTick(() => {
        if (!this.L) return

        this.map = this.L.map('map').setView([10.798043, 106.743739], 17)

        // Add OpenStreetMap tile layer
        this.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors'
        }).addTo(this.map)

        console.log('Map loaded')
      })
    },

    handleGpxFile (event) {
      const file = event.target.files[0]
      if (!file) return

      this.loading = true
      this.messages = []

      // Process GPX file
      this.parseGPX(file).then(trackPoints => {
        this.trackPoints = trackPoints
        this.trackStats = this.calculateTrackStats(trackPoints)

        this.addTrackToMap(trackPoints)
        this.addMovingMarker()

        this.showMessage(this.$t('makeKorlor.gpxLoaded'), 'success')
      }).catch(error => {
        this.showMessage(this.$t('makeKorlor.errorLoadingGpx') + error.message, 'error')
      }).finally(() => {
        this.loading = false
      })
    },

    loadDefaultMusic () {
      try {
        this.audioElement = new Audio('/ride-sound.mp3')

        // Add event listeners for music sync
        this.audioElement.addEventListener('ended', () => {
          // Repeat music when it ends, but don't stop animation
          this.musicRepeatCount++
          this.audioElement.currentTime = 0
          this.audioElement.play()
        })

        this.audioElement.addEventListener('loadeddata', () => {
          console.log('Default music loaded successfully')
        })

        this.audioElement.addEventListener('error', (error) => {
          console.error('Failed to load default music:', error)
        })
      } catch (error) {
        console.error('Error loading default music:', error)
      }
    },

    handleMusicFile (event) {
      const file = event.target.files[0]
      if (!file) return

      if (this.audioElement) {
        this.audioElement.pause()
        this.audioElement = null
      }

      this.audioElement = new Audio(URL.createObjectURL(file))

      // Add event listeners for music sync
      this.audioElement.addEventListener('ended', () => {
        // Repeat music when it ends, but don't stop animation
        this.musicRepeatCount++
        this.audioElement.currentTime = 0
        this.audioElement.play()
      })

      this.showMessage(this.$t('makeKorlor.musicLoaded'), 'success')
    },

    parseGPX (file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => {
          try {
            const xml = e.target.result
            const parser = new DOMParser()
            const xmlDoc = parser.parseFromString(xml, 'text/xml')

            const trackPoints = []
            const trkpts = xmlDoc.getElementsByTagName('trkpt')

            for (let i = 0; i < trkpts.length; i++) {
              const trkpt = trkpts[i]
              const lat = parseFloat(trkpt.getAttribute('lat'))
              const lon = parseFloat(trkpt.getAttribute('lon'))
              const eleElement = trkpt.getElementsByTagName('ele')[0]
              const ele = parseFloat((eleElement && eleElement.textContent) || 0)
              const time = trkpt.getElementsByTagName('time')[0] && trkpt.getElementsByTagName('time')[0].textContent

              if (lat && lon) {
                trackPoints.push({
                  lat: lat,
                  lon: lon,
                  ele: ele,
                  time: time,
                  index: i
                })
              }
            }

            resolve(trackPoints)
          } catch (error) {
            reject(error)
          }
        }
        reader.onerror = reject
        reader.readAsText(file)
      })
    },

    calculateDistance (lat1, lon1, lat2, lon2) {
      const R = 6371 // Earth's radius in km
      const dLat = (lat2 - lat1) * Math.PI / 180
      const dLon = (lon2 - lon1) * Math.PI / 180
      const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
               Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
               Math.sin(dLon / 2) * Math.sin(dLon / 2)
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
      return R * c
    },

    calculateSpeed (point1, point2) {
      const distance = this.calculateDistance(point1.lat, point1.lon, point2.lat, point2.lon)
      const timeDiff = new Date(point2.time) - new Date(point1.time)
      const hours = timeDiff / (1000 * 60 * 60)
      return hours > 0 ? distance / hours : 0
    },

    calculateTrackStats (points) {
      let totalDistance = 0
      let totalTime = 0
      let speeds = []
      let maxSpeed = 0

      for (let i = 1; i < points.length; i++) {
        const distance = this.calculateDistance(
          points[i - 1].lat, points[i - 1].lon,
          points[i].lat, points[i].lon
        )
        totalDistance += distance

        const timeDiff = new Date(points[i].time) - new Date(points[i - 1].time)
        totalTime += timeDiff

        const speed = this.calculateSpeed(points[i - 1], points[i])
        speeds.push(speed)
        maxSpeed = Math.max(maxSpeed, speed)
      }

      const avgSpeed = speeds.length > 0 ? speeds.reduce((a, b) => a + b, 0) / speeds.length : 0

      return {
        totalDistance: totalDistance,
        totalTime: totalTime,
        avgSpeed: avgSpeed,
        maxSpeed: maxSpeed,
        points: points
      }
    },

    playAnimation () {
      if (this.trackPoints.length === 0) {
        this.showMessage(this.$t('makeKorlor.noGpxFile'), 'error')
        return
      }

      this.isPlaying = true

      // Start animation immediately
      this.animateMovement()

      // Play music if available (will be synced by animation speed)
      if (this.audioElement) {
        this.audioElement.play()
      }
    },

    pauseAnimation () {
      this.isPlaying = false
      if (this.animationId) {
        clearTimeout(this.animationId)
      }
      if (this.audioElement) {
        this.audioElement.pause()
      }
    },

    resetAnimation () {
      this.isPlaying = false
      this.currentIndex = 0
      if (this.animationId) {
        clearTimeout(this.animationId)
      }
      if (this.audioElement) {
        this.audioElement.pause()
        this.audioElement.currentTime = 0
      }

      // Reset marker position
      if (this.trackPoints.length > 0 && this.currentMarker) {
        const firstPoint = this.trackPoints[0]
        this.currentMarker.setLatLng([firstPoint.lat, firstPoint.lon])
      }

      this.progressWidth = 0
      this.showSpeedDisplay = false
      this.musicRepeatCount = 0
    },

    addTrackToMap (points) {
      if (!this.L || !this.map) return

      // Remove existing track if any
      if (this.trackPolyline) {
        this.map.removeLayer(this.trackPolyline)
      }
      if (this.currentMarker) {
        this.map.removeLayer(this.currentMarker)
      }
      if (this.startMarker) {
        this.map.removeLayer(this.startMarker)
      }
      if (this.endMarker) {
        this.map.removeLayer(this.endMarker)
      }

      // Create track line coordinates
      const coordinates = points.map(point => [point.lat, point.lon])

      // Add track line
      this.trackPolyline = this.L.polyline(coordinates, {
        color: '#667eea',
        weight: 4,
        opacity: 0.8
      }).addTo(this.map)

      // Add start point marker (green)
      if (points.length > 0) {
        const startPoint = points[0]
        const startIcon = this.L.divIcon({
          className: 'start-marker',
          html: `<div style="background: #2ed573; width: 16px; height: 16px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; font-size: 10px;">S</div>`,
          iconSize: [16, 16],
          iconAnchor: [8, 8]
        })
        this.startMarker = this.L.marker([startPoint.lat, startPoint.lon], {
          icon: startIcon,
          title: 'Start Point'
        }).addTo(this.map)
      }

      // Add end point marker (red)
      if (points.length > 1) {
        const endPoint = points[points.length - 1]
        const endIcon = this.L.divIcon({
          className: 'end-marker',
          html: `<div style="background: #0c4a6e; width: 16px; height: 16px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; font-size: 10px;">E</div>`,
          iconSize: [16, 16],
          iconAnchor: [8, 8]
        })
        this.endMarker = this.L.marker([endPoint.lat, endPoint.lon], {
          icon: endIcon,
          title: 'End Point'
        }).addTo(this.map)
      }

      // Fit map to track bounds
      this.map.fitBounds(this.trackPolyline.getBounds(), {
        padding: [50, 50]
      })
    },

    addMovingMarker () {
      if (this.trackPoints.length === 0 || !this.L) return

      const firstPoint = this.trackPoints[0]

      // Create custom marker icon
      const markerIcon = this.L.divIcon({
        className: 'custom-marker',
        html: `<div style="background: #0c4a6e; width: ${this.markerSize}px; height: ${this.markerSize}px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 5px rgba(0,0,0,0.3);"></div>`,
        iconSize: [this.markerSize, this.markerSize],
        iconAnchor: [this.markerSize / 2, this.markerSize / 2]
      })

      this.currentMarker = this.L.marker([firstPoint.lat, firstPoint.lon], {
        icon: markerIcon
      }).addTo(this.map)
    },

    animateMovement () {
      if (!this.isPlaying) {
        return
      }

      // Check if animation is complete
      if (this.currentIndex >= this.trackPoints.length - 1) {
        this.isPlaying = false
        // Stop music when animation is complete
        if (this.audioElement) {
          this.audioElement.pause()
          this.audioElement.currentTime = 0
        }

        // Stop recording after a short delay to ensure we capture the end
        if (this.isRecording) {
          setTimeout(() => {
            if (this.isRecording) {
              console.log(this.$t('makeKorlor.animationCompleted'))
              this.stopRecording()
            }
          }, 1000) // 1 second delay to capture the final frame
        }

        return
      }

      const currentPoint = this.trackPoints[this.currentIndex]

      // Update marker position
      if (this.currentMarker) {
        this.currentMarker.setLatLng([currentPoint.lat, currentPoint.lon])
      }

      // Update speed display
      if (this.currentIndex > 0) {
        const speed = this.calculateSpeed(this.trackPoints[this.currentIndex - 1], currentPoint)
        this.currentSpeed = speed
        this.currentElevation = currentPoint.ele

        // Calculate cumulative distance
        let cumulativeDistance = 0
        for (let i = 1; i <= this.currentIndex; i++) {
          cumulativeDistance += this.calculateDistance(
            this.trackPoints[i - 1].lat, this.trackPoints[i - 1].lon,
            this.trackPoints[i].lat, this.trackPoints[i].lon
          )
        }
        this.currentDistance = cumulativeDistance

        // Update marker appearance based on speed
        this.updateMarkerForSpeed(speed)

        this.showSpeedDisplay = true
      }

      // Update progress
      this.progressWidth = (this.currentIndex / (this.trackPoints.length - 1)) * 100

      // Move camera to follow marker (with fixed duration for smooth movement)
      this.map.panTo([currentPoint.lat, currentPoint.lon], {
        animate: true,
        duration: 0.5
      })

      this.currentIndex++

      // Calculate animation delay based on speed
      const baseDelay = 50 // Increased base delay for better control
      const animationDelay = Math.max(5, baseDelay / this.animationSpeed)

      // Sync music playback rate with animation speed
      if (this.audioElement && !this.audioElement.paused) {
        this.updateMusicPlaybackRate()
      }

      // Continue animation
      this.animationId = setTimeout(() => {
        this.animateMovement()
      }, animationDelay)
    },

    updateMarkerSize () {
      if (this.currentMarker && this.L) {
        const markerIcon = this.L.divIcon({
          className: 'custom-marker',
          html: `<div style="background: #0c4a6e; width: ${this.markerSize}px; height: ${this.markerSize}px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 5px rgba(0,0,0,0.3);"></div>`,
          iconSize: [this.markerSize, this.markerSize],
          iconAnchor: [this.markerSize / 2, this.markerSize / 2]
        })
        this.currentMarker.setIcon(markerIcon)
      }
    },

    updateAnimationSpeed () {
      // Update marker appearance based on current speed
      if (this.currentIndex > 0 && this.trackPoints.length > 1) {
        const currentPoint = this.trackPoints[this.currentIndex]
        const previousPoint = this.trackPoints[this.currentIndex - 1]
        const speed = this.calculateSpeed(previousPoint, currentPoint)
        this.updateMarkerForSpeed(speed)
      }

      // Update music playback rate if playing
      if (this.audioElement && !this.audioElement.paused && this.isPlaying) {
        this.updateMusicPlaybackRate()
      }

      // If animation is currently playing, restart it with new speed
      if (this.isPlaying) {
        this.restartAnimationWithNewSpeed()
      }
    },

    restartAnimationWithNewSpeed () {
      // Store current progress
      const currentProgress = this.currentIndex / this.trackPoints.length

      // Restart animation from current position with new speed
      this.currentIndex = Math.floor(currentProgress * this.trackPoints.length)

      // Clear existing animation
      if (this.animationId) {
        clearTimeout(this.animationId)
      }

      // Continue animation with new speed
      this.animateMovement()
    },

    updateMusicPlaybackRate () {
      if (!this.audioElement || !this.isPlaying) return

      // Calculate remaining animation time
      const remainingPoints = this.trackPoints.length - this.currentIndex
      const baseDelay = 50
      const animationDelay = Math.max(5, baseDelay / this.animationSpeed)
      const remainingAnimationTime = (remainingPoints * animationDelay) / 1000

      // Calculate playback rate based on remaining time and music duration
      const musicDuration = this.audioElement.duration
      if (musicDuration > 0 && remainingAnimationTime > 0) {
        const playbackRate = musicDuration / remainingAnimationTime
        this.audioElement.playbackRate = Math.max(0.1, Math.min(4, playbackRate))
      }
    },

    updateMarkerForSpeed (speed) {
      if (!this.currentMarker || !this.L) return

      // Clear any existing speed effect timer
      if (this.speedEffectTimer) {
        clearTimeout(this.speedEffectTimer)
      }

      // Use average speed as baseline for comparison
      const avgSpeed = this.trackStats.avgSpeed || 0
      const isAboveAverage = speed > avgSpeed
      const isBelowAverage = speed < avgSpeed

      let markerColor = '#0c4a6e' // Default red
      let borderColor = 'white'
      let borderWidth = '2px'
      let boxShadow = '0 2px 5px rgba(0,0,0,0.3)'
      let animation = ''
      let icon = ''

      if (isAboveAverage) {
        // Above average speed - Green with rocket icon
        markerColor = '#2ed573'
        borderColor = '#ff6b35'
        borderWidth = '3px'
        boxShadow = '0 0 15px rgba(46,213,115,0.8), 0 2px 5px rgba(0,0,0,0.3)'
        animation = 'pulse-high 0.5s infinite'
        icon = '🚀'
        this.speedEffectActive = true
      } else if (isBelowAverage) {
        // Below average speed - Blue with chill icon
        markerColor = '#3742fa'
        borderColor = '#2ed573'
        borderWidth = '2px'
        boxShadow = '0 0 10px rgba(55,66,250,0.6), 0 2px 5px rgba(0,0,0,0.3)'
        animation = 'pulse-medium 1s infinite'
        icon = '😌'
        this.speedEffectActive = true
      } else {
        // At average speed - Normal red
        markerColor = '#0c4a6e'
        this.speedEffectActive = false
      }

      // Add speed multiplier indicator based on animation speed
      let speedIndicator = ''
      if (this.animationSpeed > 1) {
        speedIndicator = `<div style="
           position: absolute;
           top: -8px;
           right: -8px;
           background: #ff6b35;
           color: white;
           border-radius: 50%;
           width: 16px;
           height: 16px;
           display: flex;
           align-items: center;
           justify-content: center;
           font-size: 10px;
           font-weight: bold;
           border: 2px solid white;
         ">${this.animationSpeed}x</div>`
      }

      // Create marker with speed-based styling and icon
      const markerIcon = this.L.divIcon({
        className: 'custom-marker',
        html: `<div style="
           position: relative;
           background: ${markerColor}; 
           width: ${this.markerSize}px; 
           height: ${this.markerSize}px; 
           border-radius: 50%; 
           border: ${borderWidth} solid ${borderColor}; 
           box-shadow: ${boxShadow};
           animation: ${animation};
           transition: all 0.3s ease;
           display: flex;
           align-items: center;
           justify-content: center;
           font-size: ${this.markerSize * 0.7}px;
           color: white;
           font-weight: bold;
         ">${icon}${speedIndicator}</div>`,
        iconSize: [this.markerSize, this.markerSize],
        iconAnchor: [this.markerSize / 2, this.markerSize / 2]
      })

      this.currentMarker.setIcon(markerIcon)
    },

    changeMapStyle (style) {
      if (!this.L || !this.map) return

      this.map.eachLayer((layer) => {
        if (layer instanceof this.L.TileLayer) {
          this.map.removeLayer(layer)
        }
      })

      let tileLayer
      switch (style) {
        case 'satellite':
          tileLayer = this.L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
            attribution: '© Esri'
          })
          break
        case 'terrain':
          tileLayer = this.L.tileLayer('https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png', {
            attribution: '© OpenTopoMap'
          })
          break
        default:
          tileLayer = this.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '© OpenStreetMap contributors'
          })
      }

      tileLayer.addTo(this.map)
      this.currentMapStyle = style
    },

    toggleSatellite () {
      this.changeMapStyle('satellite')
    },

    toggleTerrain () {
      this.changeMapStyle('terrain')
    },

    toggleStreet () {
      this.changeMapStyle('street')
    },

    exportData () {
      if (this.trackPoints.length === 0) {
        this.showMessage(this.$t('makeKorlor.noTrackData'), 'error')
        return
      }

      const dataStr = JSON.stringify(this.trackStats, null, 2)
      const dataBlob = new Blob([dataStr], { type: 'application/json' })
      const url = URL.createObjectURL(dataBlob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'track_data.json'
      link.click()
      URL.revokeObjectURL(url)

      this.showMessage(this.$t('makeKorlor.exportSuccess'), 'success')
    },

    showMessage (text, type) {
      this.messages.push({ text, type })
      setTimeout(() => {
        this.messages = this.messages.filter(msg => msg.text !== text)
      }, 5000)
    },

    formatDateTime (timestamp) {
      if (!timestamp) return 'N/A'
      return new Date(timestamp).toLocaleString()
    },

    formatDistance (distance) {
      return `${distance.toFixed(2)} km`
    },

    formatTime (milliseconds) {
      const hours = Math.floor(milliseconds / (1000 * 60 * 60))
      const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((milliseconds % (1000 * 60)) / 1000)
      const pad = (num) => num.toString().length < 2 ? '0' + num.toString() : num.toString()
      return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
    },

    formatSpeed (speed) {
      return `${speed.toFixed(1)} km/h`
    },

    formatElevation (elevation) {
      return `${elevation.toFixed(1)} m`
    },

    cleanup () {
      if (this.animationId) {
        clearTimeout(this.animationId)
      }
      if (this.audioElement) {
        this.audioElement.pause()
      }
      if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
        this.mediaRecorder.stop()
      }
      if (this.recordingDurationInterval) {
        clearInterval(this.recordingDurationInterval)
      }
      if (this.map) {
        this.map.remove()
      }
      if (this.startMarker) {
        this.startMarker.remove()
      }
      if (this.endMarker) {
        this.endMarker.remove()
      }
    },

    toggleRecording () {
      if (!this.trackPoints.length) {
        this.showMessage(this.$t('makeKorlor.noTrackData'), 'error')
        return
      }

      if (this.isRecording) {
        console.log('Toggle: Stopping recording')
        this.stopRecording()
      } else {
        console.log('Toggle: Starting recording')
        this.startRecording()
      }
    },

    startRecording () {
      this.isRecording = true
      this.recordedChunks = []
      this.recordedVideoUrl = null
      this.recordingDuration = '00:00'
      this.recordingStartTime = Date.now()
      this.showMessage(this.$t('makeKorlor.recordingStarted'), 'info')

      // Get the map container element
      const mapContainer = document.getElementById('map')

      if (!mapContainer) {
        this.showMessage(this.$t('makeKorlor.mapNotFound'), 'error')
        this.isRecording = false
        return
      }

      try {
        // Try to automatically select the current tab
        navigator.mediaDevices.getDisplayMedia({
          video: {
            mediaSource: 'screen',
            width: { ideal: 1920 },
            height: { ideal: 1080 },
            displaySurface: 'tab',
            logicalSurface: true
          },
          audio: false,
          selfBrowserSurface: 'include',
          systemAudio: 'exclude'
        }).then(stream => {
          const options = { mimeType: 'video/webm;codecs=vp8' }
          this.mediaRecorder = new MediaRecorder(stream, options)

          this.mediaRecorder.ondataavailable = (event) => {
            console.log('Data available:', event.data.size, 'bytes')
            if (event.data.size > 0) {
              this.recordedChunks.push(event.data)
            }
          }

          this.mediaRecorder.onstop = () => {
            console.log('MediaRecorder stopped')
            if (this.recordedChunks.length > 0) {
              const blob = new Blob(this.recordedChunks, { type: 'video/webm' })
              this.recordedVideoUrl = URL.createObjectURL(blob)

              // Stop all tracks
              stream.getTracks().forEach(track => {
                console.log('Stopping track:', track.kind)
                track.stop()
              })

              this.showMessage(this.$t('makeKorlor.recordingCompleted'), 'success')
            } else {
              this.showMessage(this.$t('makeKorlor.noRecordingData'), 'error')
            }
          }

          this.mediaRecorder.onerror = (event) => {
            console.error('MediaRecorder error:', event.error)
            this.showMessage(this.$t('makeKorlor.recordingError', { error: event.error.message }), 'error')
            this.isRecording = false
          }

          this.mediaRecorder.start()
          this.startRecordingDuration()
        }).catch(error => {
          console.error('Error accessing screen:', error)
          this.showMessage(this.$t('makeKorlor.screenAccessDenied'), 'error')
          this.isRecording = false
        })
      } catch (e) {
        console.error('MediaRecorder API not supported in your browser.')
        this.showMessage(this.$t('makeKorlor.recordingNotSupported'), 'error')
        this.isRecording = false
      }
    },

    startRecordingDuration () {
      this.recordingDurationInterval = setInterval(() => {
        if (this.isRecording) {
          const elapsed = Date.now() - this.recordingStartTime
          this.recordingDuration = this.formatTime(elapsed)
        }
      }, 100)
    },

    stopRecording () {
      console.log('Stopping recording...')
      this.isRecording = false

      // Stop the media recorder
      if (this.mediaRecorder) {
        console.log('MediaRecorder state:', this.mediaRecorder.state)
        if (this.mediaRecorder.state === 'recording') {
          this.mediaRecorder.stop()
        } else if (this.mediaRecorder.state === 'paused') {
          this.mediaRecorder.stop()
        }
      }

      // Clear the duration interval
      if (this.recordingDurationInterval) {
        clearInterval(this.recordingDurationInterval)
        this.recordingDurationInterval = null
      }

      // Reset recording state
      this.recordingDuration = '00:00'
      this.recordingStartTime = 0

      this.showMessage(this.$t('makeKorlor.recordingStopped'), 'info')
    },

    downloadVideo () {
      if (!this.recordedVideoUrl) {
        this.showMessage(this.$t('makeKorlor.noVideoRecorded'), 'error')
        return
      }

      const blob = new Blob(this.recordedChunks, { type: 'video/webm' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'ride_video.webm'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      this.recordedChunks = []
      this.recordedVideoUrl = null
      this.showMessage(this.$t('makeKorlor.videoDownloaded'), 'success')
    },

    shareVideo () {
      if (!this.recordedVideoUrl) {
        this.showMessage(this.$t('makeKorlor.noVideoRecorded'), 'error')
        return
      }

      // Copy video URL to clipboard
      if (navigator.clipboard) {
        navigator.clipboard.writeText(this.recordedVideoUrl).then(() => {
          this.showMessage(this.$t('makeKorlor.videoShared'), 'success')
        }).catch(() => {
          // Fallback for older browsers
          const textArea = document.createElement('textarea')
          textArea.value = this.recordedVideoUrl
          document.body.appendChild(textArea)
          textArea.select()
          document.execCommand('copy')
          document.body.removeChild(textArea)
          this.showMessage(this.$t('makeKorlor.videoShared'), 'success')
        })
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea')
        textArea.value = this.recordedVideoUrl
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        this.showMessage(this.$t('makeKorlor.videoShared'), 'success')
      }
    },

    forceStopRecording () {
      console.log('Force stopping recording...')
      this.isRecording = false

      // Force stop media recorder
      if (this.mediaRecorder) {
        try {
          if (this.mediaRecorder.state !== 'inactive') {
            this.mediaRecorder.stop()
          }
        } catch (error) {
          console.error('Error stopping media recorder:', error)
        }
      }

      // Clear intervals
      if (this.recordingDurationInterval) {
        clearInterval(this.recordingDurationInterval)
        this.recordingDurationInterval = null
      }

      // Reset state
      this.recordingDuration = '00:00'
      this.recordingStartTime = 0

      this.showMessage(this.$t('makeKorlor.recordingForceStopped'), 'info')
    }
  }
}
</script>

<style scoped>
.gpx-animator {
  height: 100vh;
  overflow: hidden;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  touch-action: manipulation;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

.container {
  display: flex;
  height: 100vh;
  flex-direction: column;
}

.sidebar {
  width: 100%;
  max-height: 40vh;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 15px;
  overflow-y: auto;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  order: 2;
  z-index: 1000;
}

.main-content {
  flex: 1;
  position: relative;
  order: 1;
  min-height: 60vh;
}

#map {
  width: 100%;
  height: 100%;
}

.controls {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 1000;
  background: rgba(255, 255, 255, 0.95);
  padding: 10px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.speed-display {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1000;
  background: rgba(255, 255, 255, 0.95);
  color: #333;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: normal;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(5px);
  max-width: 150px;
}

.speed-alert {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 12px 20px;
  border-radius: 20px;
  font-size: 18px;
  font-weight: bold;
  text-align: center;
  animation: speedAlertFade 0.5s ease-out;
  z-index: 10000;
  pointer-events: none;
}

.speed-alert-fast {
  background: #2ed573;
  color: white;
  border: 2px solid #ff6b35;
}

.speed-alert-slow {
  background: #3742fa;
  color: white;
  border: 2px solid #2ed573;
}

.file-input, .music-input {
  margin-bottom: 15px;
}

.file-input input[type="file"], .music-input input[type="file"] {
  width: 100%;
  padding: 12px;
  border: 2px dashed #ddd;
  border-radius: 8px;
  background: #f9f9f9;
  cursor: pointer;
  font-size: 14px;
  touch-action: manipulation;
}

.file-input label, .music-input label {
  display: block;
  margin-bottom: 8px;
  font-weight: bold;
  color: #333;
  font-size: 14px;
}

.file-input input[type="file"]:hover, .music-input input[type="file"]:hover {
  border-color: #667eea;
}

.btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 12px 16px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: bold;
  margin: 3px;
  transition: all 0.3s ease;
  min-height: 44px;
  touch-action: manipulation;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.btn-icon {
  font-size: 16px;
}

.btn-text {
  font-size: 14px;
}

.map-btn {
  padding: 8px;
  min-width: 44px;
  min-height: 44px;
  border-radius: 50%;
}

.map-btn .btn-icon {
  font-size: 18px;
}

.map-btn .btn-text {
  display: none;
}

.btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.record-btn {
  background: linear-gradient(135deg, #0c4a6e 0%, #ff3742 100%);
}

.record-btn.recording {
  background: linear-gradient(135deg, #ff6b6b 0%, #0c4a6e 100%);
  animation: pulse-recording 1s infinite;
}

@keyframes pulse-recording {
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
}

.recording-info {
  background: #0c4a6e;
  color: white;
  padding: 10px;
  border-radius: 6px;
  margin-bottom: 15px;
  text-align: center;
  font-weight: bold;
}

.recording-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 8px;
}

.recording-instructions {
  font-size: 12px;
  text-align: center;
  opacity: 0.9;
}

.recording-instructions p {
  margin: 0 0 6px 0;
  line-height: 1.3;
}

.recording-instructions ul {
  margin: 0;
  padding-left: 0;
  list-style: none;
}

.recording-instructions li {
  margin: 2px 0;
  font-size: 11px;
  line-height: 1.2;
}

.force-stop-section {
  margin-top: 10px;
  text-align: center;
}

.force-stop-btn {
  background: linear-gradient(135deg, #0c4a6e 0%, #ff3742 100%);
  font-size: 12px;
  padding: 8px 12px;
  border-radius: 6px;
}

.force-stop-btn:hover {
  background: linear-gradient(135deg, #ff6b6b 0%, #0c4a6e 100%);
}

.recording-dot {
  width: 12px;
  height: 12px;
  background: white;
  border-radius: 50%;
  animation: blink 1s infinite;
}

@keyframes blink {
  0%, 50% { opacity: 1; }
  51%, 100% { opacity: 0.3; }
}

.video-controls {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 15px;
}

.video-controls h4 {
  margin-bottom: 10px;
  color: #333;
  font-size: 16px;
}

.video-actions {
  display: flex;
  gap: 8px;
  margin-top: 10px;
}

.download-btn {
  background: linear-gradient(135deg, #2ed573 0%, #1e90ff 100%);
}

.share-btn {
  background: linear-gradient(135deg, #3742fa 0%, #2ed573 100%);
}

.track-info {
  background: #f8f9fa;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 15px;
  font-size: 13px;
}

.track-info h3 {
  margin-bottom: 8px;
  color: #333;
  font-size: 16px;
}

.track-info p {
  margin: 3px 0;
  color: #666;
  font-size: 12px;
}

.animation-controls {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 15px;
  justify-content: center;
}

.slider-container {
  margin-bottom: 12px;
}

.slider-container label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
  color: #333;
  font-size: 13px;
}

.slider {
  width: 100%;
  height: 8px;
  border-radius: 4px;
  background: #ddd;
  outline: none;
  -webkit-appearance: none;
}

.slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #667eea;
  cursor: pointer;
}

.slider::-moz-range-thumb {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #667eea;
  cursor: pointer;
  border: none;
}

.progress-container {
  margin-bottom: 15px;
  text-align: center;
}

.progress-bar {
  width: 100%;
  height: 10px;
  background: #ddd;
  border-radius: 5px;
  overflow: hidden;
  margin-bottom: 8px;
  box-shadow: inset 0 1px 3px rgba(0,0,0,0.1);
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #667eea, #764ba2);
  width: 0%;
  transition: width 0.3s ease;
  border-radius: 5px;
}

.progress-container > div:last-child {
  font-size: 12px;
  color: #666;
  font-weight: bold;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #667eea, #764ba2);
  width: 0%;
  transition: width 0.3s ease;
}

.stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 15px;
}

/* Speed effect animations */
@keyframes pulse-high {
  0% { transform: scale(1); }
  50% { transform: scale(1.3); }
  100% { transform: scale(1); }
}

@keyframes pulse-medium {
  0% { transform: scale(1); }
  50% { transform: scale(1.15); }
  100% { transform: scale(1); }
}

@keyframes speedAlert {
  0% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.5);
  }
  50% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1.2);
  }
  100% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(1);
  }
}

@keyframes speedAlertFade {
  0% {
    opacity: 0;
    transform: scale(0.8);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

.stat-card {
  background: white;
  padding: 8px;
  border-radius: 6px;
  text-align: center;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.stat-value {
  font-size: 16px;
  font-weight: bold;
  color: #667eea;
}

.stat-label {
  font-size: 11px;
  color: #666;
  margin-top: 2px;
}

.loading {
  text-align: center;
  padding: 15px;
}

.spinner {
  border: 3px solid #f3f3f3;
  border-top: 3px solid #667eea;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  animation: spin 1s linear infinite;
  margin: 0 auto 10px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error {
  background: #ff6b6b;
  color: white;
  padding: 10px;
  border-radius: 6px;
  margin-bottom: 15px;
  font-size: 13px;
}

.success {
  background: #51cf66;
  color: white;
  padding: 10px;
  border-radius: 6px;
  margin-bottom: 15px;
  font-size: 13px;
}

.info {
  background: #4dabf7;
  color: white;
  padding: 10px;
  border-radius: 6px;
  margin-bottom: 15px;
  font-size: 13px;
}

/* Desktop styles */
@media (min-width: 768px) {
  .container {
    flex-direction: row;
  }

  .sidebar {
    width: 350px;
    max-height: 100vh;
    order: 1;
  }

  .main-content {
    order: 2;
    min-height: auto;
  }

  .controls {
    top: 20px;
    left: 20px;
    padding: 15px;
    flex-direction: row;
    gap: 10px;
  }

  .speed-display {
    top: 20px;
    right: 20px;
    padding: 12px 15px;
    font-size: 14px;
    max-width: none;
  }

  .file-input, .music-input {
    margin-bottom: 20px;
  }

  .file-input input[type="file"], .music-input input[type="file"] {
    padding: 10px;
    font-size: 14px;
  }

  .btn {
    padding: 12px 20px;
    margin: 5px;
    min-height: auto;
  }

  .track-info {
    padding: 15px;
    margin-bottom: 20px;
    font-size: 14px;
  }

  .track-info h3 {
    font-size: 18px;
  }

  .track-info p {
    font-size: 14px;
  }

  .animation-controls {
    gap: 10px;
    margin-bottom: 20px;
  }

  .slider-container {
    margin-bottom: 15px;
  }

  .slider-container label {
    font-size: 14px;
  }

  .progress-bar {
    margin-bottom: 10px;
  }

  .stats {
    gap: 10px;
    margin-bottom: 20px;
  }

  .stat-card {
    padding: 10px;
  }

  .stat-value {
    font-size: 18px;
  }

  .stat-label {
    font-size: 12px;
  }

  .loading {
    padding: 20px;
  }

  .error, .success, .info {
    font-size: 14px;
  }
}

/* Large mobile and tablet styles */
@media (min-width: 480px) and (max-width: 767px) {
  .sidebar {
    max-height: 35vh;
  }

  .main-content {
    min-height: 65vh;
  }

  .stats {
    grid-template-columns: repeat(2, 1fr);
  }

  .animation-controls {
    justify-content: space-between;
  }
}

/* Small mobile styles */
@media (max-width: 479px) {
  .sidebar {
    max-height: 45vh;
    padding: 10px;
  }

  .main-content {
    min-height: 55vh;
  }

  .stats {
    grid-template-columns: 1fr;
    gap: 6px;
  }

  .animation-controls {
    flex-direction: column;
    align-items: center;
  }

  .btn {
    width: 100%;
    max-width: 200px;
  }

  .btn-icon {
    font-size: 18px;
  }

  .btn-text {
    font-size: 13px;
  }

  .controls {
    top: 5px;
    left: 5px;
    padding: 8px;
  }

  .speed-display {
    top: 5px;
    right: 5px;
    padding: 6px 10px;
    font-size: 11px;
  }

  .track-info {
    padding: 10px;
    font-size: 12px;
  }

  .track-info h3 {
    font-size: 15px;
  }

  .track-info p {
    font-size: 11px;
  }

  .slider {
    height: 10px;
  }

  .slider::-webkit-slider-thumb {
    width: 28px;
    height: 28px;
  }

  .slider::-moz-range-thumb {
    width: 28px;
    height: 28px;
  }
}

/* Landscape orientation on mobile */
@media (max-height: 500px) and (orientation: landscape) {
  .sidebar {
    max-height: 30vh;
    padding: 8px;
  }

  .main-content {
    min-height: 70vh;
  }

  .track-info {
    padding: 8px;
    margin-bottom: 10px;
  }

  .stats {
    grid-template-columns: repeat(4, 1fr);
    gap: 5px;
    margin-bottom: 10px;
  }

  .stat-card {
    padding: 5px;
  }

  .stat-value {
    font-size: 12px;
  }

  .stat-label {
    font-size: 9px;
  }

  .animation-controls {
    margin-bottom: 10px;
  }

  .slider-container {
    margin-bottom: 8px;
  }
}
</style>
